import { useCallback, useEffect, useState, useRef } from "react";
import posthog from "posthog-js";
import anychart from "anychart";
import "react-day-picker/dist/style.css";
import { Button, Container, Alert } from "reactstrap";

import requests from "../requests";
import QueryMatcher from "./QueryMatcher";
import StockChartDisplay from "./StockChartDisplay";
import { useSeo } from "../hooks/useSeo";
// import DejaChartNavbar from "./DejaChartNavbar";

export default function StockQueryPage() {
  useSeo({ title: "Matcher", path: "/dashboard", noindex: true });
  const [tickers, setTickers] = useState<StockTickerData>({});
  const [query, setQuery] = useState<StockChartQuery>({} as StockChartQuery);
  const [matches, setMatches] = useState<MatchesState[]>([]);
  const [forwardDistribution, setForwardDistribution] = useState<ForwardDistribution | null>(null);
  const [forwardCurve, setForwardCurve] = useState<ForwardCurve | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const resultsRef = useRef<HTMLDivElement>(null);
  //const navigate = useNavigate();

  useEffect(() => { posthog.capture("query_matcher_opened"); }, []);

  // Initialize AnyChart theme
  useEffect(() => {
    anychart.onDocumentReady(function () {
      anychart.theme(anychart.themes.sea);
    });
  }, []);

  // Fetch available tickers
  useEffect(() => {
    async function getTickers(): Promise<void> {
      setLoading(true);
      try {
        const res = await requests.get("/api/tickers");
        setTickers(res.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching tickers:", error);
        setError("Unable to load stock tickers. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    void getTickers();
  }, []);

  // Handle scroll to results when matches are updated
  const handleSetMatches = useCallback(
    (
      matches:
        | MatchesState[]
        | ((prevMatches: MatchesState[]) => MatchesState[]),
    ) => {
      const resolved = typeof matches === "function" ? matches([]) : matches;
      setMatches(resolved);

      if (resolved.length > 0 && resultsRef.current) {
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    },
    [resultsRef],
  );

  // Logout handler
  {/*
  const handleLogout = useCallback(() => {
    // Implement your logout logic here
    navigate("/login");
  }, [navigate]);
  */}

  return (
    <div className="stock-query-page">
      {/*
      <DejaChartNavbar
        isAuthenticated={true}
        onLogout={handleLogout}
        username={username}
      />
      */}

      <div className="page-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading stock data...</p>
          </div>
        ) : error ? (
          <Container>
            <Alert color="danger" className="error-alert">
              {error}
            </Alert>
            <Button color="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </Container>
        ) : (
          <>
            <QueryMatcher
              tickers={tickers}
              setQuery={setQuery}
              setMatches={handleSetMatches}
              setForwardDistribution={setForwardDistribution}
              setForwardCurve={setForwardCurve}
              onFetchingChange={setFetching}
            />

            <div ref={resultsRef}>
              <StockChartDisplay
                query={query}
                matches={matches}
                loading={fetching}
                forwardDistribution={forwardDistribution}
                forwardCurve={forwardCurve}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
